import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Base,
  Button,
  Header,
  Link,
  Loader,
  Text,
  List,
  ListItem,
  ListItemKey,
  ListItemValue,
} from '@pet-notify/ui'

import { useCreateOrderRedemption } from 'hooks/order-redemptions'
import useDocumentTitle from 'hooks/document-title'
import { useRedemptionCodes } from 'hooks/redemption-codes'
import Address from 'components/Address'
import style from './style.module.css'
import type { OrderRedemptionInput } from 'services/order-redemtions'
import { formatMoney } from '@pet-notify/formatters'

type Props = {
  onSuccess: () => void
  state: OrderRedemptionInput
}

function ConfirmationPage({ onSuccess, state }: Props) {
  useDocumentTitle('Confirmation | Pet Notify')
  const createMutation = useCreateOrderRedemption()
  const redemptionCodesQueries = useRedemptionCodes(state.codes || [])

  const petLightPackages = useMemo(() => {
    return redemptionCodesQueries.filter((query) => {
      return query.data?.redemptionPackage.key === 'igive-2'
    })
  }, [redemptionCodesQueries])

  const handleSubmit = () => {
    createMutation.mutate(state, { onSuccess })
  }

  const isLoading = useMemo(() => {
    return redemptionCodesQueries.some(({ isLoading }) => isLoading)
  }, [redemptionCodesQueries])

  if (createMutation.isLoading || isLoading) {
    return <Loader />
  }

  return (
    <>
      <Text variant='note' mb='2'>
        Step 4 of 4
      </Text>
      <Header mb='6'>Confirmation</Header>
      <Base className={style.headerWithLink} mb='5'>
        <Header size='medium' as='h3'>
          Redemption codes
        </Header>

        <Link as={RouterLink} to='/give'>
          Edit redemption codes
        </Link>
      </Base>

      <List>
        {redemptionCodesQueries.map((query) => (
          <ListItem key={query.data?.id}>
            <ListItemKey>{query.data?.code}</ListItemKey>
            <ListItemValue>
              <Text>{query.data?.redemptionPackage.name}</Text>
            </ListItemValue>
          </ListItem>
        ))}
      </List>

      <Base className={style.headerWithLink} mb='5'>
        <Header size='medium' as='h3'>
          Account summary
        </Header>

        <Link as={RouterLink} to='/give/account'>
          Edit account
        </Link>
      </Base>

      <List>
        <ListItem>
          <ListItemKey>Name</ListItemKey>
          <ListItemValue>
            {state.user?.firstName} {state.user?.lastName}
          </ListItemValue>
        </ListItem>
        <ListItem>
          <ListItemKey>Email</ListItemKey>
          <ListItemValue>{state.user?.email}</ListItemValue>
        </ListItem>
      </List>

      <Base className={style.headerWithLink} mb='5'>
        <Header size='medium' as='h3'>
          Shipping summary
        </Header>

        <Link as={RouterLink} to='/give/shipping'>
          Edit shipping
        </Link>
      </Base>

      <List>
        <ListItem>
          <Address>{state.shippingAddress}</Address>
        </ListItem>
      </List>

      <Base className={style.headerWithLink} mb='5'>
        <Header size='medium' as='h3'>
          Order summary
        </Header>

        <Link as={RouterLink} to='/give/intellitags'>
          Edit order
        </Link>
      </Base>

      <List>
        {state.order?.intellitagOrderItems?.map(({ size, name, phone }) => (
          <ListItem key={name}>
            <ListItemKey>
              <Header size='small' as='h4' mb='2'>
                Intellitag
              </Header>
              <Text mb='1'>
                <strong>Size:</strong> {size}
              </Text>
              <Text mb='1'>
                <strong>Name:</strong> {name}
              </Text>
              {phone && (
                <Text>
                  <strong>Phone:</strong> {phone}
                </Text>
              )}
            </ListItemKey>
            <ListItemValue>
              <Text>Included in subscription</Text>
            </ListItemValue>
          </ListItem>
        ))}

        {petLightPackages.map((_, index) => (
          <ListItem key={index}>
            <ListItemKey>Pet night light</ListItemKey>
            <ListItemValue>Included in subscription</ListItemValue>
          </ListItem>
        ))}

        <ListItem>
          <ListItemKey>Shipping</ListItemKey>
          <ListItemValue>{formatMoney(0)}</ListItemValue>
        </ListItem>

        <ListItem>
          <ListItemKey>
            Premium subscription (First 12 months included)
          </ListItemKey>
          <ListItemValue>{formatMoney(0)}</ListItemValue>
        </ListItem>

        <ListItem>
          <ListItemKey>Total</ListItemKey>
          <ListItemValue>{formatMoney(0)}</ListItemValue>
        </ListItem>
      </List>

      <Button type='button' onClick={handleSubmit} mb='6'>
        Confirm order
      </Button>

      <Text>
        In confirming payment you agree to our{' '}
        <Link href='https://www.petnotify.com/terms-of-use'>Terms of Use</Link>,
        and{' '}
        <Link href='https://www.petnotify.com/privacy-policy'>
          Privacy Statement
        </Link>
        . You may modify or cancel your plan at anytime.
      </Text>
    </>
  )
}

export default ConfirmationPage
